import { KeyRound, LogOut, Monitor, Moon, Sun, UserCog } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { humanizeEnum, initials } from '@/lib/format';
import { useTheme, type ThemeChoice } from '@/providers/theme-provider';
import { useAuthStore } from '@/stores/auth-store';

export function UserMenu() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();

  if (!user) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 gap-2 px-1.5">
          <Avatar className="size-6 rounded-sm">
            <AvatarFallback className="rounded-sm bg-primary/12 tnum text-[10px] font-semibold text-primary">
              {initials(user.firstName, user.lastName)}
            </AvatarFallback>
          </Avatar>
          <span className="hidden text-xs font-medium lg:inline">{user.firstName}</span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="space-y-0.5 font-normal">
          <p className="text-[13px] font-semibold">
            {user.firstName} {user.lastName}
          </p>
          <p className="truncate text-[11px] text-muted-foreground">{user.email}</p>
          <p className="label-micro pt-1">{humanizeEnum(user.role)}</p>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        <DropdownMenuItem onSelect={() => navigate('/settings')}>
          <UserCog className="size-4" /> Profile & preferences
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate('/settings#password')}>
          <KeyRound className="size-4" /> Change password
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuLabel className="label-micro">Appearance</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={theme}
          onValueChange={(value) => setTheme(value as ThemeChoice)}
        >
          <DropdownMenuRadioItem value="light">
            <Sun className="size-4" /> Light
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark">
            <Moon className="size-4" /> Dark
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="system">
            <Monitor className="size-4" /> System
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          variant="destructive"
          onSelect={async () => {
            await logout();
            navigate('/login', { replace: true });
          }}
        >
          <LogOut className="size-4" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
